// ContractNoteManagerPopup.js
import React, { useEffect, useMemo, useState } from "react";
import "./css/ContractNoteManagerPopup.css";
import { apiFetchJson } from "apiClient";
import EditContractNote from "./EditContractNote";
import { deleteContractNote } from "@portfolio/utils/PFService";
import { useStackedPopup } from "@dashboard/contexts/PopupStackContext"; // adjust path

const PRESETS = {
    LAST_WEEK: "LAST_WEEK",
    LAST_MONTH: "LAST_MONTH",
    LAST_YEAR: "LAST_YEAR",
    CUSTOM: "CUSTOM",
};

function toIsoDate(d) {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

function computePresetRange(preset) {
    const today = new Date();
    const end = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const start = new Date(end);

    if (preset === PRESETS.LAST_WEEK) {
        start.setDate(start.getDate() - 7);
    } else if (preset === PRESETS.LAST_MONTH) {
        start.setMonth(start.getMonth() - 1);
    } else if (preset === PRESETS.LAST_YEAR) {
        start.setFullYear(start.getFullYear() - 1);
    } else {
        // CUSTOM handled outside
        return { from: null, to: null };
    }

    return {
        from: toIsoDate(start),
        to: toIsoDate(end),
    };
}

const ContractNoteManagerPopup = ({ isOpen, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [notes, setNotes] = useState([]);
    const [error, setError] = useState(null);

    const [preset, setPreset] = useState(PRESETS.LAST_WEEK);
    const [customFrom, setCustomFrom] = useState("");
    const [customTo, setCustomTo] = useState("");

    const [selectedIds, setSelectedIds] = useState(new Set());
    const [editingDto, setEditingDto] = useState(null);

    const effectiveRange = useMemo(() => {
        if (preset === PRESETS.CUSTOM) {
            if (!customFrom || !customTo) return { from: null, to: null };
            return { from: customFrom, to: customTo };
        }
        return computePresetRange(preset);
    }, [preset, customFrom, customTo]);

    useStackedPopup(isOpen, () => {
        // If nested EditContractNote is open, close that first
        if (editingDto) {
            setEditingDto(null);
            // returning nothing is fine; the popup remains registered
            return;
        }
        if (onClose) {
            onClose();
        }
    });

    useEffect(() => {
        if (!isOpen) return;
        if (!effectiveRange.from || !effectiveRange.to) return;

        let cancelled = false;

        const loadNotes = async () => {
            setLoading(true);
            setError(null);
            try {
                // 🔹 You can adjust the endpoint name/params as per your backend.
                const qs = new URLSearchParams({
                    from: effectiveRange.from,
                    to: effectiveRange.to,
                }).toString();

                const data = await apiFetchJson(`/api/portfolio/contractNotesByDateRange?${qs}`);

                if (!cancelled) {
                    // Expecting array of DTOs: { contractNote, transactions, cnotesCharge }
                    setNotes(Array.isArray(data) ? data : []);
                    setSelectedIds(new Set());
                }
            } catch (e) {
                console.error("Failed to fetch contract notes by date range", e);
                if (!cancelled) {
                    setError("Failed to load contract notes for selected range.");
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        loadNotes();

        return () => {
            cancelled = true;
        };
    }, [isOpen, effectiveRange.from, effectiveRange.to]);

    if (!isOpen) return null;

    const fetchLatestDto = async (contractNoteNo) => {
        try {
            const result = await apiFetchJson(
                `/api/portfolio/getContractNote?contractNoteNo=${encodeURIComponent(contractNoteNo)}`
            );

            // Normalize structure if needed
            if (!result) return null;

            // Case: backend wraps response
            const dto = result.data || result.dto || result;

            // Ensure required structure exists
            if (!dto.contractNote) {
                console.error("Backend did not return a valid DTO:", dto);
                alert("Backend returned invalid contract note format.");
                return null;
            }

            return dto;
        } catch (e) {
            console.error("Failed to fetch latest ContractNoteDTO:", e);
            alert("❌ Error loading contract note. Check logs.");
            return null;
        }
    };


    const toggleSelectOne = (cnNo) => {
        setSelectedIds(prev => {
            const copy = new Set(prev);
            if (copy.has(cnNo)) copy.delete(cnNo);
            else copy.add(cnNo);
            return copy;
        });
    };

    const toggleSelectAll = (checked) => {
        if (!checked) {
            setSelectedIds(new Set());
            return;
        }
        const all = new Set(
            notes
                .map(dto => dto?.contractNote?.contractNoteNo)
                .filter(Boolean)
        );
        setSelectedIds(all);
    };

    const handleDeleteSelected = async () => {
        if (selectedIds.size === 0) {
            alert("Select at least one contract note to delete.");
            return;
        }

        // NOTE: deleteContractNote already shows a confirmation per note.
        // If you want a single confirmation only, you can add it here and
        // tweak deleteContractNote to accept a flag to skip confirm.
        for (const cnNo of selectedIds) {
            try {
                const result = await deleteContractNote(cnNo);
                if (!result || !result.success) {
                    console.warn("Delete failed for", cnNo, result);
                }
            } catch (e) {
                console.error("Error deleting", cnNo, e);
            }
        }

        // Refresh list after deletion
        if (effectiveRange.from && effectiveRange.to) {
            try {
                const qs = new URLSearchParams({
                    from: effectiveRange.from,
                    to: effectiveRange.to,
                }).toString();
                const data = await apiFetchJson(`/api/portfolio/contractNotesByDateRange?${qs}`);
                setNotes(Array.isArray(data) ? data : []);
                setSelectedIds(new Set());
            } catch (e) {
                console.error("Failed to reload after delete", e);
            }
        }
    };

    const handleEditSelected = () => {
        if (selectedIds.size === 0) {
            alert("Select one contract note to edit.");
            return;
        }
        if (selectedIds.size > 1) {
            alert("Please select only one contract note to edit at a time.");
            return;
        }
        const [cnNo] = Array.from(selectedIds);
        const dto = notes.find(n => n?.contractNote?.contractNoteNo === cnNo);
        if (!dto) {
            alert("Could not locate selected contract note data.");
            return;
        }
        setEditingDto(dto);
    };

    const handleRowEditClick = async (dto) => {
        const cnNo = dto?.contractNote?.contractNoteNo;
        if (!cnNo) return;

        setSelectedIds(new Set([cnNo]));

        const latestDto = await fetchLatestDto(cnNo);
        console.log("DEBUG DTO:", latestDto);
        if (latestDto) {
            setEditingDto(latestDto);
        }
    };


    const formatDate = (value) => {
        if (!value) return "";
        try {
            const d = new Date(value);
            if (!isNaN(d.getTime())) {
                return d.toLocaleDateString("en-GB", {
                    weekday: "short",
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                });
            }
        } catch (e) {
            // ignore
        }
        return String(value);
    };

    return (
        <div className="cn-mgr-overlay">
            <div className="cn-mgr-dialog">
                {/* Header */}
                <div className="cn-mgr-header">
                    <div className="cn-mgr-title">
                        Manage Imported Contract Notes
                    </div>
                    <button
                        className="cn-mgr-close-btn"
                        onClick={onClose}
                        title="Close"
                    >
                        ✕
                    </button>
                </div>

                {/* Filters */}
                <div className="cn-mgr-filters">
                    <div className="cn-mgr-preset-buttons">
                        <button
                            type="button"
                            className={
                                preset === PRESETS.LAST_WEEK
                                    ? "cn-mgr-preset-btn active"
                                    : "cn-mgr-preset-btn"
                            }
                            onClick={() => setPreset(PRESETS.LAST_WEEK)}
                        >
                            Last Week
                        </button>
                        <button
                            type="button"
                            className={
                                preset === PRESETS.LAST_MONTH
                                    ? "cn-mgr-preset-btn active"
                                    : "cn-mgr-preset-btn"
                            }
                            onClick={() => setPreset(PRESETS.LAST_MONTH)}
                        >
                            Last Month
                        </button>
                        <button
                            type="button"
                            className={
                                preset === PRESETS.LAST_YEAR
                                    ? "cn-mgr-preset-btn active"
                                    : "cn-mgr-preset-btn"
                            }
                            onClick={() => setPreset(PRESETS.LAST_YEAR)}
                        >
                            Last Year
                        </button>
                        <button
                            type="button"
                            className={
                                preset === PRESETS.CUSTOM
                                    ? "cn-mgr-preset-btn active"
                                    : "cn-mgr-preset-btn"
                            }
                            onClick={() => setPreset(PRESETS.CUSTOM)}
                        >
                            Custom Range
                        </button>
                    </div>

                    {preset === PRESETS.CUSTOM && (
                        <div className="cn-mgr-custom-range">
                            <label>
                                From:
                                <input
                                    type="date"
                                    value={customFrom}
                                    onChange={(e) => setCustomFrom(e.target.value)}
                                />
                            </label>
                            <label>
                                To:
                                <input
                                    type="date"
                                    value={customTo}
                                    onChange={(e) => setCustomTo(e.target.value)}
                                />
                            </label>
                        </div>
                    )}
                </div>

                {/* Body */}
                <div className="cn-mgr-body">
                    {loading && (
                        <div className="cn-mgr-loading">
                            <div className="cn-mgr-spinner" />
                            <span>Loading contract notes…</span>
                        </div>
                    )}

                    {error && !loading && (
                        <div className="cn-mgr-error">
                            {error}
                        </div>
                    )}

                    {!loading && !error && notes.length === 0 && (
                        <div className="cn-mgr-empty">
                            No contract notes found for the selected range.
                        </div>
                    )}

                    {!loading && !error && notes.length > 0 && (
                        <div className="cn-mgr-table-wrapper">
                            <table className="cn-mgr-table">
                                <thead>
                                <tr>
                                    <th>
                                        <input
                                            type="checkbox"
                                            checked={
                                                selectedIds.size > 0 &&
                                                selectedIds.size === notes.length
                                            }
                                            onChange={(e) => toggleSelectAll(e.target.checked)}
                                        />
                                    </th>
                                    <th>Date</th>
                                    <th>Client ID</th>
                                    <th>Contract Note No</th>
                                    <th>Broker</th>
                                    <th>Net Amount</th>
                                    <th>Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {notes.map((dto, idx) => {
                                    const cn = dto.contractNote || {};
                                    const charges = dto.cnotesCharge || {};
                                    const cnNo = cn.contractNoteNo;
                                    const isChecked = cnNo && selectedIds.has(cnNo);

                                    return (
                                        <tr key={cnNo || idx}>
                                            <td>
                                                {cnNo && (
                                                    <input
                                                        type="checkbox"
                                                        checked={isChecked}
                                                        onChange={() => toggleSelectOne(cnNo)}
                                                    />
                                                )}
                                            </td>
                                            <td>{formatDate(cn.tradeDate)}</td>
                                            <td>{cn.clientId}</td>
                                            <td>{cn.contractNoteNo}</td>
                                            <td>{cn.brokerName}</td>
                                            <td>{charges.netAmountNetTotal ?? ""}</td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="cn-mgr-row-btn"
                                                    onClick={() => handleRowEditClick(dto)}
                                                >
                                                    Edit
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="cn-mgr-footer">
                    <div className="cn-mgr-footer-left">
                        {selectedIds.size} selected
                    </div>
                    <div className="cn-mgr-footer-right">
                        <button
                            type="button"
                            className="cn-mgr-footer-btn danger"
                            onClick={handleDeleteSelected}
                        >
                            Delete Selected
                        </button>
                    </div>
                </div>
            </div>

            {/* Nested EditContractNote popup when editing a specific note */}
            {editingDto && editingDto.contractNote && (
                <EditContractNote
                    clientId={editingDto.contractNote.clientId}
                    contractNoteDTO={editingDto}
                    onClose={() => setEditingDto(null)}
                />
            )}
        </div>
    );
};

export default ContractNoteManagerPopup;
